import {
  Injectable,
  NgZone,
  Type,
} from '@angular/core';
import { Router } from '@angular/router';
// import @ngrx
// import @ngrx
import {
  Actions,
  createEffect,
  ofType,
} from '@ngrx/effects';
import {
  Action,
  select,
  Store,
} from '@ngrx/store';
import {
  asyncScheduler,
  combineLatest as observableCombineLatest,
  Observable,
  of,
  queueScheduler,
  timer,
} from 'rxjs';
import {
  catchError,
  delay,
  filter,
  map,
  observeOn,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { AppState } from '../../app.reducer';
import {
  hasValue,
  isNotNull,
} from '../../shared/empty.util';
import { NotificationsActionTypes } from '../../shared/notifications/notifications.actions';
import { StoreActionTypes } from '../../store.actions';
import { AuthorizationDataService } from '../data/feature-authorization/authorization-data.service';
import { RequestActionTypes } from '../data/request.actions';
import { EPerson } from '../eperson/models/eperson.model';
import { EnterZoneScheduler } from '../utilities/enter-zone.scheduler';
import { LeaveZoneScheduler } from '../utilities/leave-zone.scheduler';
// import actions
import {
  AuthActionTypes,
  AuthenticateAction,
  AuthenticatedAction,
  AuthenticatedErrorAction,
  AuthenticatedSuccessAction,
  AuthenticationErrorAction,
  AuthenticationSuccessAction,
  AuthErrorActionsWithErrorPayload,
  CheckAuthenticationTokenCookieAction,
  LogOutErrorAction,
  LogOutSuccessAction,
  RedirectAfterLoginSuccessAction,
  RefreshEpersonAndTokenRedirectAction,
  RefreshEpersonAndTokenRedirectErrorAction,
  RefreshEpersonAndTokenRedirectSuccessAction,
  RefreshTokenAction,
  RefreshTokenAndRedirectAction,
  RefreshTokenAndRedirectErrorAction,
  RefreshTokenAndRedirectSuccessAction,
  RefreshTokenErrorAction,
  RefreshTokenSuccessAction,
  RetrieveAuthenticatedEpersonAction,
  RetrieveAuthenticatedEpersonErrorAction,
  RetrieveAuthenticatedEpersonSuccessAction,
  RetrieveAuthMethodsAction,
  RetrieveAuthMethodsErrorAction,
  RetrieveAuthMethodsSuccessAction,
  RetrieveTokenAction,
  SetRedirectUrlAndNavigateAction,
  SetUserAsIdleAction,
} from './auth.actions';
// import services
import { AuthService } from './auth.service';
import { AuthMethod } from './models/auth.method';
import { AuthStatus } from './models/auth-status.model';
import { AuthTokenInfo } from './models/auth-token-info.model';
import {
  getAuthenticatedUser,
  isAuthenticated,
  isAuthenticatedLoaded,
} from './selectors';

// Action Types that do not break/prevent the user from an idle state
const IDLE_TIMER_IGNORE_TYPES: string[]
  = [...Object.values(AuthActionTypes).filter((t: string) => t !== AuthActionTypes.UNSET_USER_AS_IDLE),
    ...Object.values(RequestActionTypes), ...Object.values(NotificationsActionTypes)];

export function errorToAuthAction$<T extends AuthErrorActionsWithErrorPayload>(actionType: Type<T>, error: unknown): Observable<T> {
  if (error instanceof Error) {
    return of(new actionType(error));
  }

  // If we caught something that's not an Error: complain & drop type safety
  console.warn('AuthEffects caught non-Error object:', error);
  return of(new actionType(error as any));
}

@Injectable()
export class AuthEffects {

  /**
   * Authenticate user.
   * @method authenticate
   */
  public authenticate$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(AuthActionTypes.AUTHENTICATE),
    switchMap((action: AuthenticateAction) => {
      return this.authService.authenticate(action.payload.email, action.payload.password).pipe(
        take(1),
        map((response: AuthStatus) => new AuthenticationSuccessAction(response.token)),
        catchError((error: unknown) => errorToAuthAction$(AuthenticationErrorAction, error)),
      );
    }),
  ));

  public authenticateSuccess$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(AuthActionTypes.AUTHENTICATE_SUCCESS),
    map((action: AuthenticationSuccessAction) => new AuthenticatedAction(action.payload)),
  ));

  public authenticated$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(AuthActionTypes.AUTHENTICATED),
    switchMap((action: AuthenticatedAction) => {
      return this.authService.authenticatedUser(action.payload).pipe(
        map((userHref: string) => new AuthenticatedSuccessAction((userHref !== null), action.payload, userHref)),
        catchError((error: unknown) => {
          if (action.checkAgain) {
            return of(new CheckAuthenticationTokenCookieAction());
          } else {
            return errorToAuthAction$(AuthenticatedErrorAction, error);
          }
        }),
      );
    }),
  ));

  public authenticatedSuccess$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(AuthActionTypes.AUTHENTICATED_SUCCESS),
    tap((action: AuthenticatedSuccessAction) => this.authService.storeToken(action.payload.authToken)),
    switchMap((action: AuthenticatedSuccessAction) => this.authService.getRedirectUrl().pipe(
      take(1),
      map((redirectUrl: string) => [action, redirectUrl]),
    )),
    map(([action, redirectUrl]: [AuthenticatedSuccessAction, string]) => {
      if (hasValue(redirectUrl)) {
        return new RedirectAfterLoginSuccessAction(redirectUrl);
      } else {
        return new RetrieveAuthenticatedEpersonAction(action.payload.userHref);
      }
    }),
  ));

  public redirectAfterLoginSuccess$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(AuthActionTypes.REDIRECT_AFTER_LOGIN_SUCCESS),
    tap((action: RedirectAfterLoginSuccessAction) => {
      this.authService.clearRedirectUrl();
      this.authService.navigateToRedirectUrl(action.payload);
    }),
  ), { dispatch: false });

  public redirectAndNavigate$: Observable<Action> = createEffect(() => this.actions$
    .pipe(ofType(AuthActionTypes.SET_REDIRECT_URL_AND_NAVIGATE),
      tap((action: SetRedirectUrlAndNavigateAction) => this.router.navigate([decodeURIComponent(action.payload.navigateUrl)])),
    ),
  { dispatch: false },
  );

  // It means "reacts to this action but don't send another"
  public authenticatedError$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(AuthActionTypes.AUTHENTICATED_ERROR),
    tap((action: LogOutSuccessAction) => this.authService.removeToken()),
  ), { dispatch: false });

  public retrieveAuthenticatedEperson$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(AuthActionTypes.RETRIEVE_AUTHENTICATED_EPERSON),
    switchMap((action: RetrieveAuthenticatedEpersonAction) => {
      const impersonatedUserID = this.authService.getImpersonateID();
      let user$: Observable<EPerson>;
      if (hasValue(impersonatedUserID)) {
        user$ = this.authService.retrieveAuthenticatedUserById(impersonatedUserID);
      } else {
        user$ = this.authService.retrieveAuthenticatedUserByHref(action.payload);
      }
      return user$.pipe(
        take(1),
        map((user: EPerson) => new RetrieveAuthenticatedEpersonSuccessAction(user)),
        catchError((error: unknown) => errorToAuthAction$(RetrieveAuthenticatedEpersonErrorAction, error)),
      );
    }),
  ));

  public checkToken$: Observable<Action> = createEffect(() => this.actions$.pipe(ofType(AuthActionTypes.CHECK_AUTHENTICATION_TOKEN),
    switchMap(() => {
      return this.authService.hasValidAuthenticationToken().pipe(
        map((token: AuthTokenInfo) => new AuthenticatedAction(token, true)),
        catchError((error: unknown) => of(new CheckAuthenticationTokenCookieAction())),
      );
    }),
  ));

  public checkTokenCookie$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(AuthActionTypes.CHECK_AUTHENTICATION_TOKEN_COOKIE),
    switchMap(() => {
      return this.authService.checkAuthenticationCookie().pipe(
        map((response: AuthStatus) => {
          if (response.authenticated) {
            this.authService.setExternalAuthStatus(true);
            this.authorizationsService.invalidateAuthorizationsRequestCache();
            return new RetrieveTokenAction();
          } else {
            return new RetrieveAuthMethodsAction(response);
          }
        }),
        catchError((error: unknown) => errorToAuthAction$(AuthenticatedErrorAction, error)),
      );
    }),
  ));

  public retrieveToken$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(AuthActionTypes.RETRIEVE_TOKEN),
    switchMap((action: AuthenticateAction) => {
      return this.authService.refreshAuthenticationToken(null).pipe(
        take(1),
        map((token: AuthTokenInfo) => new AuthenticationSuccessAction(token)),
        catchError((error: unknown) => errorToAuthAction$(AuthenticationErrorAction, error)),
      );
    }),
  ));

  public refreshToken$: Observable<Action> = createEffect(() => this.actions$.pipe(ofType(AuthActionTypes.REFRESH_TOKEN),
    switchMap((action: RefreshTokenAction) => {
      return this.authService.refreshAuthenticationToken(action.payload).pipe(
        take(1),
        map((token: AuthTokenInfo) => new RefreshTokenSuccessAction(token)),
        catchError((error: unknown) => of(new RefreshTokenErrorAction())),
      );
    }),
  ));

  // It means "reacts to this action but don't send another"
  public refreshTokenSuccess$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(AuthActionTypes.REFRESH_TOKEN_SUCCESS),
    tap((action: RefreshTokenSuccessAction) => this.authService.replaceToken(action.payload)),
  ), { dispatch: false });

  /**
   * When the store is rehydrated in the browser,
   * clear a possible invalid token or authentication errors
   */
  public clearInvalidTokenOnRehydrate$: Observable<any> = createEffect(() => this.actions$.pipe(
    ofType(StoreActionTypes.REHYDRATE),
    switchMap(() => {
      const isLoaded$ = this.store.pipe(select(isAuthenticatedLoaded));
      const authenticated$ = this.store.pipe(select(isAuthenticated));
      return observableCombineLatest([isLoaded$, authenticated$]).pipe(
        take(1),
        filter(([loaded, authenticated]) => loaded && !authenticated),
        tap(() => this.authService.removeToken()),
        tap(() => this.authService.resetAuthenticationError()),
      );
    })), { dispatch: false });

  /**
   * When the store is rehydrated in the browser, invalidate all cache hits regarding the
   * authorizations endpoint, to be sure to have consistent responses after a login with external idp
   *
   */
  invalidateAuthorizationsRequestCache$ = createEffect(() => this.actions$
    .pipe(ofType(StoreActionTypes.REHYDRATE),
      tap(() => this.authorizationsService.invalidateAuthorizationsRequestCache()),
    ), { dispatch: false });

  public logOut$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(AuthActionTypes.LOG_OUT),
      switchMap(() => {
        this.authService.stopImpersonating();
        return this.authService.logout().pipe(
          map(() => new LogOutSuccessAction()),
          catchError((error: unknown) => errorToAuthAction$(LogOutErrorAction, error)),
        );
      }),
    ));

  public logOutSuccess$: Observable<Action> = createEffect(() => this.actions$
    .pipe(ofType(AuthActionTypes.LOG_OUT_SUCCESS),
      tap(() => this.authService.removeToken()),
      tap(() => this.authService.clearRedirectUrl()),
      tap(() => this.authService.refreshAfterLogout()),
    ), { dispatch: false });

  public redirectToLoginTokenExpired$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(AuthActionTypes.REDIRECT_TOKEN_EXPIRED),
      tap(() => this.authService.removeToken()),
      tap(() => this.authService.redirectToLoginWhenTokenExpired()),
    ), { dispatch: false });

  public retrieveMethods$: Observable<Action> = createEffect(() => this.actions$
    .pipe(
      ofType(AuthActionTypes.RETRIEVE_AUTH_METHODS),
      switchMap((action: RetrieveAuthMethodsAction) => {
        return this.authService.retrieveAuthMethodsFromAuthStatus(action.payload)
          .pipe(
            map((authMethodModels: AuthMethod[]) => new RetrieveAuthMethodsSuccessAction(authMethodModels)),
            catchError(() => of(new RetrieveAuthMethodsErrorAction())),
          );
      }),
    ));

  public refreshTokenAndRedirect$: Observable<Action> = createEffect(() => this.actions$
    .pipe(ofType(AuthActionTypes.REFRESH_TOKEN_AND_REDIRECT),
      switchMap((action: RefreshTokenAndRedirectAction) => {
        return this.authService.refreshAuthenticationToken(action.payload.token)
          .pipe(
            take(1),
            map((token: AuthTokenInfo) => new RefreshTokenAndRedirectSuccessAction(token, action.payload.redirectUrl)),
            catchError(() => of(new RefreshTokenAndRedirectErrorAction())),
          );
      })),
  );

  public refreshStateTokenRedirect$: Observable<Action> = createEffect(() => this.actions$
    .pipe(ofType(AuthActionTypes.REFRESH_EPERSON_AND_TOKEN_REDIRECT),
      map(({ payload }: RefreshEpersonAndTokenRedirectAction) => payload),
      withLatestFrom(this.store.pipe(select(getAuthenticatedUser), filter(isNotNull))),
      switchMap(([{ token, redirectUrl }, { id }]) =>
        this.authService.retrieveAuthenticatedUserById(id).pipe(
          take(1),
          map(user => new RefreshEpersonAndTokenRedirectSuccessAction(user, token, redirectUrl)),
          catchError(() => of(new RefreshEpersonAndTokenRedirectErrorAction())),
        ),
      ),
    ),
  );

  public refreshStateTokenRedirectSuccess$: Observable<Action> = createEffect(() => this.actions$
    .pipe(ofType(AuthActionTypes.REFRESH_EPERSON_AND_TOKEN_REDIRECT_SUCCESS),
      map((action: RefreshEpersonAndTokenRedirectAction) => new RefreshTokenAndRedirectAction(action.payload.token, action.payload.redirectUrl))),
  );

  public refreshTokenAndRedirectSuccess$: Observable<Action> = createEffect(() => this.actions$
    .pipe(ofType(AuthActionTypes.REFRESH_TOKEN_AND_REDIRECT_SUCCESS),
      tap((action: RefreshTokenAndRedirectSuccessAction) => this.authService.replaceToken(action.payload.token)),
      delay(1),
      tap((action: RefreshTokenAndRedirectSuccessAction) => this.router.navigate([decodeURIComponent(action.payload.redirectUrl)])),
    ),
  { dispatch: false },
  );

  /**
   * For any action that is not in {@link IDLE_TIMER_IGNORE_TYPES} that comes in => Start the idleness timer
   * If the idleness timer runs out (so no un-ignored action come through for that amount of time)
   * => Return the action to set the user as idle ({@link SetUserAsIdleAction})
   * @method trackIdleness
   */
  public trackIdleness$: Observable<Action> = createEffect(() => this.actions$.pipe(
    filter((action: Action) => !IDLE_TIMER_IGNORE_TYPES.includes(action.type)),
    // Using switchMap the effect will stop subscribing to the previous timer if a new action comes
    // in, and start a new timer
    switchMap(() =>
      // Start a timer outside of Angular's zone
      timer(environment.auth.ui.timeUntilIdle, new LeaveZoneScheduler(this.zone, asyncScheduler)),
    ),
    // Re-enter the zone to dispatch the action
    observeOn(new EnterZoneScheduler(this.zone, queueScheduler)),
    map(() => new SetUserAsIdleAction()),
  ));

  /**
   * @constructor
   * @param {Actions} actions$
   * @param {NgZone} zone
   * @param {AuthorizationDataService} authorizationsService
   * @param {AuthService} authService
   * @param {Store} store
   * @param {Router} router
   */
  constructor(private actions$: Actions,
              private zone: NgZone,
              private authorizationsService: AuthorizationDataService,
              private authService: AuthService,
              private store: Store<AppState>,
              private router: Router) {
  }
}
